import { Component, Input, HostListener, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MediaItem } from 'libs/interfaces/src/lib/task.interface';
import { Subscription } from 'rxjs';
import { ImageViewerService } from 'libs/shared-services/src/lib/image-viewer/image-viewer.service';


interface ImageItem {
  url: string;
  caption?: string;
}

@Component({
    selector: 'lib-image-viewer',
    imports: [CommonModule],
    templateUrl: './imageViewer.component.html'
})
export class ImageViewerComponent implements OnInit, OnDestroy {
  @Input() topOffset: number = 0;
  // Remove @Input() for images/startIndex if using the service
  // @Input() images: (string | MediaItem)[] = [];
  // @Input() startIndex = 0;

  images: (string | MediaItem)[] = [];
  startIndex = 0;
  currentIndex = 0;
  scale = 1;
  isVisible = false;

  private subscriptions: Subscription[] = [];

  // Touch handling variables
  private touchStartX = 0;
  private touchStartY = 0;
  private touchStartDistance = 0;
  private initialScale = 1;
  private isSwiping = false;
  private isZooming = false;

  constructor(private imageViewerService: ImageViewerService) {}

  ngOnInit() {
    this.subscriptions.push(
      this.imageViewerService.images$.subscribe(images => {
        this.images = images;
      }),
      this.imageViewerService.startIndex$.subscribe(idx => {
        this.startIndex = idx;
        this.currentIndex = idx;
      }),
      this.imageViewerService.isVisible$.subscribe(visible => {
        this.isVisible = visible;
        if (visible) {
          this.scale = 1;
          document.body.style.overflow = 'hidden';
        } else {
          document.body.style.overflow = 'auto';
          this.scale = 1;
        }
      })
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  // Touch event handlers
  onTouchStart(event: TouchEvent) {
    if (event.touches.length === 2) {
      // Pinch to zoom
      this.isZooming = true;
      this.isSwiping = false;
      this.touchStartDistance = this.getTouchDistance(event.touches);
      this.initialScale = this.scale;
    } else if (event.touches.length === 1) {
      // Single touch for swipe
      this.isZooming = false;
      this.isSwiping = true;
      this.touchStartX = event.touches[0].clientX;
      this.touchStartY = event.touches[0].clientY;
    }
  }

  onTouchMove(event: TouchEvent) {
    event.preventDefault(); // Prevent scrolling while interacting with image

    if (this.isZooming && event.touches.length === 2) {
      // Handle pinch zoom
      const currentDistance = this.getTouchDistance(event.touches);
      const scaleFactor = currentDistance / this.touchStartDistance;
      this.scale = Math.min(Math.max(this.initialScale * scaleFactor, 0.5), 3);
    } else if (this.isSwiping && event.touches.length === 1) {
      // Handle swipe
      const deltaX = event.touches[0].clientX - this.touchStartX;
      const deltaY = event.touches[0].clientY - this.touchStartY;
      // Only handle horizontal swipes if not zoomed in
      if (Math.abs(deltaX) > Math.abs(deltaY) && this.scale <= 1) {
        event.preventDefault();
        if (Math.abs(deltaX) > 50) { // Minimum swipe distance
          if (deltaX > 0 && this.currentIndex > 0) {
            this.previous();
            this.isSwiping = false;
          } else if (deltaX < 0 && this.currentIndex < this.images.length - 1) {
            this.next();
            this.isSwiping = false;
          }
        }
      }
    }
  }

  onTouchEnd() {
    this.isZooming = false;
    this.isSwiping = false;
  }

  private getTouchDistance(touches: TouchList): number {
    const dx = touches[1].clientX - touches[0].clientX;
    const dy = touches[1].clientY - touches[0].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  show(index: number = 0) {
    this.currentIndex = index;
    this.isVisible = true;
    this.scale = 1;
    document.body.style.overflow = 'hidden';
  }

  close() {
    this.isVisible = false;
    document.body.style.overflow = 'auto';
    this.scale = 1;
    this.imageViewerService.close(); // Ensure service state is updated
  }

  next() {
    if (this.currentIndex < this.images.length - 1) {
      this.currentIndex++;
      this.scale = 1;
    }
  }

  previous() {
    if (this.currentIndex > 0) {
      this.currentIndex--;
      this.scale = 1;
    }
  }

  zoomIn() {
    this.scale = Math.min(this.scale + 0.25, 3);
  }

  zoomOut() {
    this.scale = Math.max(this.scale - 0.25, 0.5);
  }

  resetZoom() {
    this.scale = 1;
  }

  getCurrentImage(): string {
    const current = this.images[this.currentIndex];
    return typeof current === 'string' ? current : current.mediaId?.imageUrl || '';
  }

  getCurrentCaption(): string | undefined {
    const current = this.images[this.currentIndex];
    return typeof current === 'string' ? undefined : current.mediaId?.name || current.subletId?.name;
  }

  getCurrentImageFilename(): string {
    const url = this.getCurrentImage();
    if (!url) return 'image.jpg';
    try {
      return decodeURIComponent(url.split('/').pop() || 'image.jpg');
    } catch {
      return 'image.jpg';
    }
  }

  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    if (!this.isVisible) return;

    switch (event.key) {
      case 'ArrowRight':
        this.next();
        break;
      case 'ArrowLeft':
        this.previous();
        break;
      case 'Escape':
        this.close();
        break;
      case '+':
        this.zoomIn();
        break;
      case '-':
        this.zoomOut();
        break;
      case '0':
        this.resetZoom();
        break;
    }
  }

  // iOS detection
  isIOS(): boolean {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
  }

  openImageInNewTab() {
    window.open(this.getCurrentImage(), '_blank');
  }
}
